import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { black, FlexAlignCentered, useViewportWidth } from "@stepfinance/react";
import type { DropdownProps } from "antd";
import { Button, Dropdown } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { usePageVisibility } from "react-page-visibility";
import { useMatch } from "react-router-dom";

import { useNetworkConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useRefreshContext, useRefreshUpdateContext } from "../../contexts/refresh";
import { useSimplifiedViewContext } from "../../contexts/simplifiedView";
import { HeaderLogoComponent } from "../../layouts/AppLayout/HeaderLogoComponent";
import { notify } from "../../utils/notifications";
import { CurrentUserBadge } from "../CurrentUserBadge";
import { SettingsIcon } from "../Icons";
import { AddressInput } from "./AddressInput";
import { headerButtonStyles, RefreshButton } from "./RefreshButton";
import { SettingsMenu } from "./SettingsMenu";
import { WalletConnect } from "./WalletConnect";
import { MobileWalletMenu } from "./WalletMenu";
import { WalletMenuButton } from "./WalletMenuButton";

export type AppBarProps = {
  refreshDisabled?: boolean;
};

export const AppBar: React.FC<AppBarProps> = () => {
  const { connection } = useConnection();
  const { network } = useNetworkConfig();
  const { isSimplifiedViewActive } = useSimplifiedViewContext();
  const { publicKey, connected } = useEnrichedWallet();
  const isVisible = usePageVisibility();
  const { refreshing } = useRefreshContext();
  const refreshUpdate = useRefreshUpdateContext();
  const { currentViewport } = useViewportWidth();
  const transactionHistoryMatch = useMatch({ path: "/history", end: false });

  const isMobile = currentViewport === "Mobile";

  const {
    currentLanguageData: {
      pages: { allPages },
    },
  } = useLanguageContext();

  const refreshDisabled = useMemo(() => transactionHistoryMatch !== null, [transactionHistoryMatch]);

  const [timerKey, setTimerKey] = useState(0);

  const refreshContexts = async () => {
    setTimerKey((prevKey) => prevKey + 1);
    await refreshUpdate();
  };

  const dropdownProps: DropdownProps = {
    overlayStyle: {
      paddingTop: "18px",
    },
    placement: "bottomRight",
    trigger: ["click"],
    mouseEnterDelay: 0,
  };

  const iconProps = {
    height: 16,
    width: 16,
  };

  const airdrop = useCallback(() => {
    if (!publicKey) {
      return;
    }

    void connection.requestAirdrop(publicKey, 1 * LAMPORTS_PER_SOL).then(() => {
      notify({
        message: "SOL airdropped",
        type: "success",
      });
    });
  }, [publicKey, connection]);

  return (
    <AppBarContainer>
      {isMobile ? (
        <>
          <HeaderLogoComponent />
          <FlexAlignCentered style={{ gap: 5 }}>
            <RefreshButton
              isVisible={isVisible}
              refreshContext={refreshing}
              refreshContexts={refreshContexts}
              refreshDisabled={refreshDisabled || !connected}
              timerKey={timerKey}
            />
            <HeaderButton {...dropdownProps} overlay={<SettingsMenu />}>
              <Button type="default" icon={<SettingsIcon {...iconProps} />} />
            </HeaderButton>
            <HeaderButton {...dropdownProps} overlay={<MobileWalletMenu />}>
              {!connected ? (
                <WalletMenuButton width={125}>{allPages.topBar.walletButton.connectWallet}</WalletMenuButton>
              ) : (
                <WalletConnect>
                  <CurrentUserBadge />
                </WalletConnect>
              )}
            </HeaderButton>
          </FlexAlignCentered>
        </>
      ) : isSimplifiedViewActive ? (
        <SettingsButtonOnlyCont>
          <HeaderButton overlay={<SettingsMenu />}>
            <Button icon={<SettingsIcon {...iconProps} />} />
          </HeaderButton>
        </SettingsButtonOnlyCont>
      ) : (
        <>
          <FlexAlignCentered>
            <AddressInput />
          </FlexAlignCentered>
          <HeaderButtonsContainer>
            {network !== "mainnet-beta" && (
              <WalletMenuButton onClick={airdrop}>
                <span style={{ fontWeight: "bold" }}>Faucet</span>
              </WalletMenuButton>
            )}

            {connected && !refreshDisabled && (
              <RefreshButton
                isVisible={isVisible}
                refreshContext={refreshing}
                refreshContexts={refreshContexts}
                refreshDisabled={refreshDisabled}
                timerKey={timerKey}
              />
            )}
            <HeaderButton {...dropdownProps} overlay={<SettingsMenu />}>
              <Button icon={<SettingsIcon {...iconProps} />} />
            </HeaderButton>
            <WalletConnect>
              <CurrentUserBadge />
            </WalletConnect>
          </HeaderButtonsContainer>
        </>
      )}
    </AppBarContainer>
  );
};

const AppBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  background: ${black};
  padding: 20px;
`;

const SettingsButtonOnlyCont = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

const HeaderButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
`;

const HeaderButton = styled(Dropdown)`
  ${headerButtonStyles}
`;
